/**
 * contracts.md §1a — the **one** generator, the **one** display formatter, and the **one**
 * input normalizer for a `GateId`.
 *
 * A `GateId` is a **randomly generated eight-digit decimal code**, `^[0-9]{8}$`. It is
 * deliberately numeric-only so it cannot be mistaken for the six-character alphanumeric
 * `BrokerCode` by eye, in speech, or in a support transcript.
 *
 * **A GateId is a string of digits, never a number.** Leading zeros are significant:
 * `09348172` and `9348172` are not the same identifier, and the second is not a GateId at all.
 * It is stored as a DynamoDB `S` attribute, serialized as a JSON string, and never parsed with
 * `parseInt`, `Number()`, or a spreadsheet's numeric coercion. Every export path — support
 * tooling, T1's export, a CSV opened in Excel — must round-trip the leading zero. The only
 * integer in this file lives inside `generateGateId`, between the random draw and
 * `padStart`; it never escapes.
 *
 * **Three functions, and each of them is the only one of its kind:**
 *
 *  - `generateGateId()` — the provisioning draw. Uniqueness is **not** its job (see below).
 *  - `formatGateId()` — the grouped `NNNN NNNN` presentation. Its output is never stored,
 *    sent, compared, or used as a key.
 *  - `normalizeGateIdInput()` — the edge repair, applied **once**, at the boundary. Storage
 *    and comparison downstream are exact.
 *
 * **Uniqueness is enforced by conditional write, never by a prior read.** 10⁸ is smaller than
 * `BrokerCode`'s 36⁶ ≈ 2.18 × 10⁹ *on purpose* — matching the vendor's capacity has no
 * engineering value, and the two lengths that would match it are the two to avoid (nine digits
 * reads as a תעודת זהות, ten as an Israeli mobile number, in a product whose identifier for a
 * person *is* a phone number). The cost is that collisions are **expected, not exceptional**:
 * ~0.5% across D59's whole 1,000-gate pilot cohort, and roughly one provisioning in a hundred
 * at 10⁶ gates. P3's bounded regenerate-and-retry is therefore required behavior, not a
 * defensive nicety.
 *
 * **A GateId is not a capability.** Eight digits are short enough to guess. That is acceptable
 * only because knowing one grants nothing: every gate-scoped route authorizes on the caller's
 * membership grant, never on possession of the identifier, a non-member's read is
 * indistinguishable from a non-existent gate's, and gate-scoped routes are rate-limited per
 * caller (`rate-limit.ts`). Those two rules are what make the short numeric space safe.
 *
 * **There is no embedded creation time and no meaningful ordering.** Unlike a ULID, a GateId
 * sorts arbitrarily and leaks nothing about when a gate was provisioned; anything that needs
 * creation order reads `createdAt`. The absence of a timestamp is a privacy improvement.
 */
import { asGateId } from './keys.js';
/** The canonical form. The bare eight digits are the whole identifier — there is no prefix. */
export const GATE_ID_PATTERN = /^[0-9]{8}$/;
export const GATE_ID_LENGTH = 8;
/** `00000000`–`99999999`, inclusive. The zero-padding is part of the value, not a format step. */
const GATE_ID_SPACE = 100_000_000;
/**
 * 🔸 Codes that read as something other than a gate.
 *
 * **Author-introduced and vetoable**: it costs one set lookup per provision and removes 27 of
 * 100,000,000 candidates. Two structural families (a run of one digit, a consecutive ascending
 * or descending run) plus the Israeli emergency short codes padded to length, which are the
 * sequences a resident would misread when a support agent reads one aloud.
 *
 * It is a **screen on generation only.** It is deliberately *not* a validation rule: a denied
 * value is still a well-formed GateId, and rejecting one at read time would strand any gate
 * that predates a later addition to this list.
 */
function buildDenyList() {
    const denied = new Set();
    for (let digit = 0; digit < 10; digit += 1) {
        denied.add(String(digit).repeat(GATE_ID_LENGTH));
    }
    for (let start = 0; start < 10; start += 1) {
        let ascending = '';
        let descending = '';
        for (let step = 0; step < GATE_ID_LENGTH; step += 1) {
            ascending += String((start + step) % 10);
            descending += String((start - step + 10 * GATE_ID_LENGTH) % 10);
        }
        denied.add(ascending);
        denied.add(descending);
    }
    // Police, MDA, fire, electric-company fault, municipal hotline, and the two international
    // codes an Israeli handset also accepts.
    for (const emergency of ['100', '101', '102', '103', '104', '106', '110', '112', '911']) {
        denied.add(emergency.padEnd(GATE_ID_LENGTH, '0'));
    }
    return denied;
}
const DENIED = buildDenyList();
/** Whether the generator would redraw this candidate. Exposed so the screen is testable. */
export function isDeniedGateId(candidate) {
    return DENIED.has(candidate);
}
/**
 * The default draw: a cryptographically seeded, **unbiased** integer in `[0, 10⁸)`.
 *
 * **Why Web Crypto rather than `node:crypto`'s `randomInt`.** This module is the one place
 * §1a puts the generator, the display formatter, and the input normalizer — and the latter two
 * are browser-facing, so the module is loaded by the PWA. A top-level `import` of `node:crypto`
 * makes the whole module unbundleable for the browser: Vite externalizes the specifier and the
 * named import then fails at link time, before tree-shaking can drop the unused generator.
 * `globalThis.crypto` is the *same* CSPRNG on both sides — Node ≥ 19 exposes Web Crypto
 * globally — so this costs no entropy quality and keeps §1a's one-file rule intact.
 *
 * **The modulo is rejected, not taken.** `value % 10⁸` over the full 32-bit range would favour
 * the low `2³² mod 10⁸` values; drawing again above the largest exact multiple of the space
 * removes that bias entirely. The expected number of redraws is under 1.08, and the loop cannot
 * be unbounded in practice for the same reason.
 */
function drawUniformGateIdValue() {
    // The largest multiple of GATE_ID_SPACE that fits in a uint32. Draws at or above it are the
    // partial final bucket and would skew the result low, so they are discarded.
    const limit = Math.floor(0x1_0000_0000 / GATE_ID_SPACE) * GATE_ID_SPACE;
    const buffer = new Uint32Array(1);
    for (;;) {
        globalThis.crypto.getRandomValues(buffer);
        const value = buffer[0];
        if (value < limit)
            return value % GATE_ID_SPACE;
    }
}
/**
 * Draw a candidate GateId.
 *
 * **This does not check that the code is free**, and no caller may add a lookup to make it do
 * so: a read-then-write passes a single-threaded test and produces two gates under concurrent
 * provisioning. The transaction's `attribute_not_exists` on the gate item is the uniqueness
 * check; a condition failure attributable to that item means *draw again*.
 *
 * The default draw is `drawUniformGateIdValue` — Web Crypto, rejection-sampled, and
 * **unbiased** across the full range, which `Math.random() * n` is not.
 *
 * @param draw Test seam. Must return an integer in `[0, 100000000)`.
 */
export function generateGateId(draw = drawUniformGateIdValue) {
    // Bounded rather than `while (true)`: 27 denied values against 10⁸ cannot exhaust this, so
    // spinning here would mean the injected draw is broken, and a hang is the worst way to
    // report that.
    for (let attempt = 0; attempt < 8; attempt += 1) {
        const value = draw();
        if (!Number.isInteger(value) || value < 0 || value >= GATE_ID_SPACE) {
            throw new TypeError('A GateId draw must be an integer in [0, 100000000)');
        }
        // `padStart` is where the identifier becomes a string, and it never becomes a number
        // again. Roughly one draw in ten needs it, which is exactly why the leading-zero rule is
        // stated everywhere it is: numeric handling destroys ~10% of the keyspace silently.
        const candidate = String(value).padStart(GATE_ID_LENGTH, '0');
        if (!isDeniedGateId(candidate))
            return asGateId(candidate);
    }
    throw new Error('GateId generation drew a denied code eight times: the draw is not random');
}
/**
 * The grouped presentation — `4830 2917`, two groups of four.
 *
 * **Presentation only.** The separator is a **non-breaking** space so the identifier never
 * wraps mid-code in a narrow column or a support transcript, and that character is precisely
 * why this form must never be stored, sent, compared, or used as a key: it is not the
 * identifier, it looks exactly like it, and a `U+00A0` inside a partition key is unfindable by
 * eye.
 *
 * Callers render the result LTR and bidi-isolated inside the Hebrew UI (`dir="ltr"` on an
 * isolating element); the grouping alone does not make a Latin-digit run safe in an RTL
 * paragraph.
 */
export function formatGateId(id) {
    // The separator is written as an escape, never as a literal: a bare U+00A0 in source
    // is invisible, and an editor or formatter that helpfully replaces it with an
    // ordinary space would break wrapping with nothing visible in the diff.
    return `${id.slice(0, 4)}\u00A0${id.slice(4)}`;
}
/**
 * Repair a typed GateId, **once, at the edge**.
 *
 * Two classes of repair and nothing else:
 *
 *  1. **Separators** — spaces (including the non-breaking one `formatGateId` emits, so a
 *     copy-paste of the displayed form round-trips) and hyphens are removed.
 *  2. **Digit look-alikes** a user may type despite the numeric alphabet: `O`/`o` → `0`,
 *     `I`/`i`/`L`/`l` → `1`.
 *
 * Arbitrary non-digits are **not** discarded. That is the whole design: stripping everything
 * that is not a digit would silently repair `48302917x` and `4-8-3-0-2-9-1-7-!` into a valid
 * code, so junk would name a real gate instead of failing. Anything left over after the two
 * repairs above must already match `^[0-9]{8}$`.
 *
 * Idempotent: normalizing a normalized value returns it unchanged.
 *
 * Throws `TypeError`, which the boundary translates — a malformed id on a gate-scoped path is
 * a `404` (nothing about which gates exist is observable from the shape of a refusal), and in
 * a body it is D57's `422` naming the field and never echoing the value.
 */
export function normalizeGateIdInput(raw) {
    const repaired = raw
        // `\s` already covers the non-breaking space `formatGateId` emits, so the displayed
        // form round-trips. The trailing `-` is a literal hyphen, not a range.
        .replace(/[\s-]/g, '')
        .replace(/[Oo]/g, '0')
        .replace(/[IiLl]/g, '1');
    if (!GATE_ID_PATTERN.test(repaired)) {
        throw new TypeError('GateId must normalize to eight digits');
    }
    return asGateId(repaired);
}
//# sourceMappingURL=gate-id.js.map